import React from 'react';
import { ErrorState } from './ErrorState';

export type ErrorBoundaryVariant = 'page' | 'section' | 'item';

export interface AppErrorBoundaryProps {
  children: React.ReactNode;
  variant?: ErrorBoundaryVariant;
  title?: string;
  message?: string;
  onReload?: () => void;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

const COPY: Record<ErrorBoundaryVariant, { title: string; message: string; retryLabel: string }> = {
  page: {
    title: 'Algo salió mal',
    message: 'La aplicación encontró un error inesperado. Puedes recargar para continuar.',
    retryLabel: 'Recargar',
  },
  section: {
    title: 'No se pudo mostrar esta vista',
    message: 'Esta sección falló al renderizar. El resto de Zunelo sigue disponible.',
    retryLabel: 'Reintentar',
  },
  item: {
    title: 'No se pudo mostrar este elemento',
    message: 'Esta tarjeta falló al renderizar. Las demás siguen visibles.',
    retryLabel: 'Reintentar',
  },
};

export class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    return {
      hasError: true,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }

  componentDidCatch(error: Error): void {
    console.error('[AppErrorBoundary]', this.props.variant ?? 'page', error.message);
  }

  private handleRetry = (): void => {
    const variant = this.props.variant ?? 'page';
    if (variant === 'page') {
      (this.props.onReload ?? (() => window.location.reload()))();
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;

    const variant = this.props.variant ?? 'page';
    const copy = COPY[variant];
    const detail = this.state.error?.message;

    return (
      <div data-testid="error-boundary" data-variant={variant}>
        <ErrorState
          title={this.props.title ?? copy.title}
          message={this.props.message ?? copy.message}
          retryLabel={copy.retryLabel}
          onRetry={this.handleRetry}
        />
        {import.meta.env.DEV && detail ? (
          <p
            data-testid="error-boundary-detail"
            style={{
              margin: '0 18px 12px',
              color: 'var(--z-dim)',
              fontSize: 11,
              lineHeight: 1.4,
              overflowWrap: 'anywhere',
            }}
          >
            Detalle técnico: {detail}
          </p>
        ) : null}
      </div>
    );
  }
}
